import { useState } from 'react';
import {
  Button, Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Toolbar } from '@vnetwork/ui/layout';
import { DataTable, StatusPill, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import { useOpsCdnConnections, type OpsEagleConnection } from '@/api/operations';
import { useOpsConnectCdnService } from '@/api/operations-org-hooks';

type StatusFilter = '' | 'disconnected' | 'connected';

// Select needs non-empty string values; 'all' maps to the '' (no filter) status.
const STATUS_OPTIONS = [
  { value: 'disconnected', label: 'Disconnected' },
  { value: 'connected', label: 'Connected' },
  { value: 'all', label: 'All' },
];

/**
 * CDN → Connections tab — fleet view of every Eagle CDN service and whether it
 * has its own console account/credential (connected) vs the shared reseller key
 * (disconnected). Per-row "Connect" mints the missing account + credential
 * (idempotent, Tech-gated on the backend).
 */
export function CdnConnectionsTab() {
  const [status, setStatus] = useState<StatusFilter>('disconnected');
  const { data, isLoading, isError, refetch } = useOpsCdnConnections(status);
  const connect = useOpsConnectCdnService();
  const [connectingId, setConnectingId] = useState<string | null>(null);

  const handleConnect = async (serviceId: string) => {
    setConnectingId(serviceId);
    try {
      await connect.mutateAsync({ serviceId });
    } catch {
      // Error surfaced via the banner below; row stays actionable.
    } finally {
      setConnectingId(null);
    }
  };

  const columns: ColumnDef<OpsEagleConnection>[] = [
    {
      id: 'customer',
      header: 'Customer',
      cell: ({ row }) => (
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
            {row.original.customer?.customer_name || `Org ${row.original.organization_id}`}
          </span>
          <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
            {row.original.organization_id}
          </span>
        </div>
      ),
    },
    { id: 'service', header: 'Service', accessorKey: 'service_name' },
    {
      id: 'entity',
      header: 'Entity',
      cell: ({ row }) => (
        <span style={{ fontFamily: 'var(--vn-font-mono)' }}>{row.original.entity_id || '—'}</span>
      ),
    },
    {
      id: 'connected',
      header: 'Connection',
      cell: ({ row }) => (
        <StatusPill tone={row.original.connected ? 'success' : 'warning'}>
          {row.original.connected ? 'Connected' : 'Disconnected'}
        </StatusPill>
      ),
    },
    {
      id: 'action',
      header: '',
      cell: ({ row }) =>
        row.original.connected ? null : (
          <Button
            variant="primary"
            size="sm"
            disabled={connectingId === row.original.service_id}
            onClick={() => handleConnect(row.original.service_id)}
          >
            {connectingId === row.original.service_id ? 'Connecting…' : 'Connect'}
          </Button>
        ),
    },
  ];

  const rows = data ?? [];
  const disconnectedCount = rows.filter((r) => !r.connected).length;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar style={{ marginBottom: 0 }}>
        <Select
          value={status === '' ? 'all' : status}
          onValueChange={(v) => setStatus(v === 'all' ? '' : (v as StatusFilter))}
        >
          <SelectTrigger aria-label="Connection status filter" style={{ width: 200 }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Toolbar>

      {connect.isError && (
        <Alert tone="danger">Failed to connect a service. Please retry.</Alert>
      )}

      {isLoading ? (
        <TabLoading rows={6} />
      ) : isError ? (
        <Alert tone="danger">
          Failed to load connections.{' '}
          <Button variant="ghost" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </Alert>
      ) : rows.length === 0 ? (
        <EmptyState title="No services" description="No Eagle services match this filter." />
      ) : (
        <>
          {status !== 'connected' && disconnectedCount > 0 && (
            <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              {disconnectedCount} service(s) not yet connected.
            </span>
          )}
          <DataTable columns={columns} data={rows} />
        </>
      )}
    </div>
  );
}
